<template>
  <v-container fluid>
    <!-- dialog z edycją wybranego elementu -->
    <v-dialog v-model="editDialog" persistent scrollable max-width="1200px">
      <form v-if="editedItem" id="edit-form" name="editForm" role="form" @submit.prevent="updateEditedItem()">
        <v-card height="85vh">
          <v-toolbar color="surface-variant">
            <v-toolbar-title v-if="editedItem.menuId.id">{{ $t('admin-menus.edit.title') }}</v-toolbar-title>
            <v-toolbar-title v-if="!editedItem.menuId.id">{{ $t('admin-menus.edit.create') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeEditItemDialog(false)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-container>
              <v-card>
                <v-card-text>
                  <v-select
                    v-if="!isRootMenu"
                    v-model="editedItem.actionType"
                    :items="availableMenuActionTypeOptions"
                    :label="$t('admin-menus.edit.actionType')"
                  ></v-select>

                  <div v-if="editedItem.actionType == 'MENU_RESOURCE'">
                    <v-text-field
                      :model-value="selectedResource ? selectedResource.name : editedItem.resourceId?.uuid"
                      readonly
                      clearable
                      :label="$t('admin-menus.edit.resource')"
                      hide-details="auto"
                      @click:clear="clearResource"
                    >
                      <template v-slot:append>
                        <v-btn icon variant="text" @click="chooseResource">
                          <v-icon>mdi-database-search</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                    <div v-if="selectedResource" class="text-body-2 text-medium-emphasis ml-4 mb-2">
                      {{ selectedResource.uuid }}
                    </div>
                    <resource-picker ref="resourcePicker" />
                  </div>

                  <v-text-field
                    v-model="v$.editedItem.name.$model"
                    clearable
                    :label="$t('admin-menus.edit.name')"
                    :error-messages="editedItemNameErrors"
                  >
                    <template v-slot:append>
                      <languages-edit-dialog
                        v-model:translations="editedItem.translations"
                        :title="$t('component-languages.edit.langs')"
                        icon-only
                      />
                    </template>
                  </v-text-field>

                  <thumbnail-picker-field
                    :model-value="editedItem.thumbnail?.uuid"
                    :label="$t('admin-menus.edit.thumbnail')"
                    :error-messages="editedItemThumbnailErrors"
                    @update:model-value="val => editedItem.thumbnail = { uuid: val ?? '' }"
                  />

                  <v-select
                    v-if="!isRootMenu && editedItem.actionType !== 'MENU_RESOURCE'"
                    class="mt-4"
                    v-model="editedItem.contentLayout"
                    :items="availableMenuContentLayoutOptions"
                    :label="$t('admin-menus.edit.contentLayout')"
                  ></v-select>

                  <v-text-field class="mt-4" v-model="editedItem.sortNo" clearable :label="$t('admin-menus.edit.sortNo')"></v-text-field>

                  <v-checkbox v-model="editedItem.active" :label="$t('admin-menus.edit.active')"></v-checkbox>

                  <v-checkbox
                    v-if="isRootMenu"
                    v-model="editedItem.publicDefault"
                    :label="$t('admin-menus.edit.publicDefault')"
                  ></v-checkbox>

                  <autocomplete-multiple
                    v-if="editedItem.actionType == 'FILTER'"
                    :items="allMenuFilters"
                    item-value="filterId.id"
                    item-title="filterName"
                    v-model="selectedMenuFilterIds"
                    :label="$t('admin-menus.edit.filters')"
                  />

                </v-card-text>
              </v-card>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="outlined" @click="closeEditItemDialog(false)" >{{ $t('admin-menus.edit.cancel') }}</v-btn>
            <v-btn variant="flat" color="primary" type="submit" :disabled="v$.$invalid" >{{ $t('admin-menus.edit.save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </form>
    </v-dialog>

  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, inject, onMounted, PropType } from 'vue';
import { useI18n } from '@/shared/composables/i18n';
import type {
  AdminMenuItemUpdateRequest,
  MenuItemFilterValue,
  MenuItemValue,
  ResourceDto,
  ResourceFilterValue,
} from '@/venit/services/model/techpubs-cms';
import { FilterGroupingType, MenuActionType, MenuContentLayout } from '@/venit/services/model/techpubs-cms';
import { maxLength, maxValue, minLength, minValue, required } from '@vuelidate/validators';
import MenuManagementService from './menu-management.service';
import Validators from '@/venit/validators/validators';
import LanguagesEditDialog from '@/venit/components/lang-edit/languages-edit-dialog.vue';
import FiltersManagementService from '@/venit/admin/filters-management/filters-management.service';
import AutocompleteMultiple from '@/shared/components/AutocompleteMultiple.vue';
import ThumbnailPickerField from '@/venit/components/thumbnail-picker/thumbnail-picker-field.vue';
import ResourcePickerDialog from '@/venit/components/resource-picker/resource-picker-dialog.vue';
import ResourceManagementService from '@/venit/admin/resource-management/resource-management.service';
import { useVuelidate } from '@vuelidate/core';

const mustBeUuid = (value: string) => Validators.checkIfValidUuidOrEmpty(value);

export default defineComponent({
  name: 'MenuManagementEdit',

  components: {
    'thumbnail-picker-field': ThumbnailPickerField,
    'languages-edit-dialog': LanguagesEditDialog,
    'resource-picker': ResourcePickerDialog,
    'autocomplete-multiple': AutocompleteMultiple,
  },

  props: {
    editDialog: {
      type: Boolean,
      required: true,
    },
    selectedMenuItem: {
      type: Object as PropType<MenuItemValue>,
      required: true,
    },
  },

  emits: ['closeEditMenuItem'],

  setup(props, { emit }) {
    const { t: $t } = useI18n();

    const menuManagementService = inject<() => MenuManagementService>('menuManagementService')!;
    const filtersManagementService = inject<() => FiltersManagementService>('filtersService')!;
    const resourcesManagementService = inject<() => ResourceManagementService>('resourceService')!;

    const editedItem = ref<MenuItemValue | null>(null);
    const allMenuFilters = ref<ResourceFilterValue[]>([]);
    const selectedMenuFilterIds = ref<string[]>([]);
    const selectedResource = ref<{ name: string; uuid: string } | null>(null);
    const resourcePicker = ref<InstanceType<typeof ResourcePickerDialog> | null>(null);
    const activeOptions = ['True', 'False'];

    const validationRules = computed(() => ({
      editedItem: {
        name: {
          required,
          minLength: minLength(1),
          maxLength: maxLength(254),
        },
        actionType: {
          required,
        },
        contentLayout: {
          required: (val: string) => editedItem.value?.actionType === MenuActionType.MENU_RESOURCE || !!val,
        },
        thumbnail: {
          uuid: {
            mustBeUuid,
          },
        },
        sortNo: {
          required,
          minValue: minValue(0),
          maxValue: maxValue(1000000),
        },
      },
    }));

    const v$ = useVuelidate(validationRules, { editedItem });

    const copyMenuItemValues = (item: MenuItemValue): MenuItemValue => {
      return JSON.parse(JSON.stringify(item));
    };

    const listAllMenuFilters = () => {
      filtersManagementService()
        .listAllMenuFilters()
        .then(response => {
          allMenuFilters.value = response.data.filters;
        });
    };

    const loadSelectedResource = (uuid: string | null | undefined) => {
      if (!uuid) {
        selectedResource.value = null;
        return;
      }
      resourcesManagementService()
        .readResource({ resourceId: { uuid } })
        .then(response => {
          const rv = response.data.resourceValue;
          selectedResource.value = { name: rv.name, uuid: rv.uuid };
        });
    };

    const chooseResource = async () => {
      const resource: ResourceDto | null = await resourcePicker.value?.openDefault(editedItem.value?.resourceId ?? undefined);
      if (resource && editedItem.value) {
        selectedResource.value = resource;
        editedItem.value.resourceId = { uuid: resource.uuid };
      }
    };

    const clearResource = () => {
      selectedResource.value = null;
      if (editedItem.value) {
        editedItem.value.resourceId = null;
      }
    };

    const showEditItemDialog = (item: MenuItemValue) => {
      listAllMenuFilters();
      editedItem.value = copyMenuItemValues(item);
      selectedMenuFilterIds.value = editedItem.value.menuFilters.map(mf => mf.filterId.id);
      loadSelectedResource(editedItem.value.resourceId?.uuid);
    };

    const closeEditItemDialog = (saved: boolean) => {
      emit('closeEditMenuItem', saved);
    };

    const updateEditedItem = () => {
      if (!editedItem.value) return;

      if (editedItem.value.actionType == MenuActionType.FILTER) {
        const selectedFiltersValues: MenuItemFilterValue[] = selectedMenuFilterIds.value.map(fid => {
          return {
            menuId: editedItem.value!.menuId,
            filterId: {
              id: fid,
            },
            filterType: FilterGroupingType.MANDATORY,
          };
        });
        editedItem.value.menuFilters = selectedFiltersValues;
        editedItem.value.resourceId = null;
      } else if (editedItem.value.actionType == MenuActionType.MENU_RESOURCE) {
        editedItem.value.menuFilters = [];
        editedItem.value.contentLayout = MenuContentLayout.NONE;
      } else {
        editedItem.value.menuFilters = [];
        editedItem.value.resourceId = null;
      }

      const requestData: AdminMenuItemUpdateRequest = {
        menuItem: editedItem.value,
      };

      menuManagementService()
        .updateMenu(requestData)
        .then(() => {
          closeEditItemDialog(true);
        });
    };

    const availableMenuActionTypeOptions = computed(() => {
      return Object.keys(MenuActionType).filter(
        k => ![MenuActionType.MENU_TEMPLATE_ROOT, MenuActionType.FILE, MenuActionType.CORTONA_MODEL].find(c => c == k)
      );
    });

    const availableMenuContentLayoutOptions = computed(() => {
      return Object.keys(MenuContentLayout)
        .filter(k => k != MenuContentLayout.NONE)
        .filter(
          k =>
            !(
              editedItem.value &&
              (editedItem.value.actionType == MenuActionType.FILTER ||
                editedItem.value.actionType == MenuActionType.CORTONA_MODEL ||
                editedItem.value.actionType == MenuActionType.MENU_RESOURCE) &&
              k == MenuContentLayout.TREE
            )
        );
    });

    const isRootMenu = computed(() => {
      return editedItem.value?.actionType == MenuActionType.MENU_TEMPLATE_ROOT;
    });

    const editedItemNameErrors = computed(() => {
      const errors: string[] = [];
      if (!v$.value.editedItem.name.$dirty) return errors;
      if (v$.value.editedItem.name.required.$invalid) errors.push($t('admin-menus.edit.nameRequired'));
      return errors;
    });

    const editedItemThumbnailErrors = computed(() => {
      const errors: string[] = [];
      if (!v$.value.editedItem.thumbnail.uuid.$dirty) return errors;
      if (v$.value.editedItem.thumbnail.uuid.mustBeUuid.$invalid) errors.push($t('admin-menus.edit.invalidThumbnailId'));
      return errors;
    });

    watch(
      () => props.selectedMenuItem,
      (value) => {
        showEditItemDialog(value);
      }
    );

    onMounted(() => {
      showEditItemDialog(props.selectedMenuItem);
    });

    return {
      editedItem,
      allMenuFilters,
      selectedMenuFilterIds,
      selectedResource,
      resourcePicker,
      activeOptions,
      v$,
      closeEditItemDialog,
      updateEditedItem,
      chooseResource,
      clearResource,
      availableMenuActionTypeOptions,
      availableMenuContentLayoutOptions,
      isRootMenu,
      editedItemNameErrors,
      editedItemThumbnailErrors,
    };
  },
});
</script>
